import axios from 'axios';
import { BASE_URL, ENDPOINTS } from './apiConfig';

/**
 * Get all departments for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of departments
 */
export const getAllDepartments = async (companyId) => {
  if (!companyId) return [];

  try {
    const endpoint = ENDPOINTS && ENDPOINTS.GET_ALL_DEPARTMENTS;
    const url = endpoint
      ? `${BASE_URL}${endpoint.replace(':company_id', companyId)}`
      : `${BASE_URL}departments/${companyId}`;

    const token = localStorage.getItem('token');

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data ?? [];
  } catch (error) {
    if (error.response?.status === 404) return [];
    const message = error.response?.data?.error || error.message || 'Failed to fetch departments';
    throw new Error(message);
  }
};

/**
 * Create a new department
 * @param {Object} departmentData - { name, description, companyId, head (optional) }
 * @returns {Promise<Object>} Created department
 */
export const createDepartment = async (departmentData) => {
  try {
    const { companyId, name, description, head } = departmentData;
    const url = `${BASE_URL}${ENDPOINTS.CREATE_DEPARTMENT.replace(':company_id', companyId)}`;
    const token = localStorage.getItem('token');
    
    const response = await axios.post(url, {
      dep_name: name,
      head: head || undefined
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    const message = error.response?.data?.error || error.message || 'Failed to create department';
    throw new Error(message);
  }
};

/**
 * Update an existing department
 * @param {string} departmentId - The department ID
 * @param {Object} updateData - { name, description, head (optional) }
 * @returns {Promise<Object>} Updated department
 */
export const updateDepartment = async (departmentId, updateData) => {
  try {
    const { name, description, head } = updateData;
    const url = `${BASE_URL}${ENDPOINTS.UPDATE_DEPARTMENT.replace(':department_id', departmentId)}`;
    const token = localStorage.getItem('token');
    
    const response = await axios.put(url, {
      dep_name: name,
      head: head || undefined
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    const message = error.response?.data?.error || error.message || 'Failed to update department';
    throw new Error(message);
  }
};

/**
 * Delete a department
 * @param {string} departmentId - The department ID
 * @returns {Promise<Object>} Deletion response
 */
export const deleteDepartment = async (departmentId) => {
  try {
    const url = `${BASE_URL}${ENDPOINTS.DELETE_DEPARTMENT.replace(':department_id', departmentId)}`;
    const token = localStorage.getItem('token');
    
    const response = await axios.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    const message = error.response?.data?.error || error.message || 'Failed to delete department';
    throw new Error(message);
  }
};

/**
 * Add employees to a department
 * @param {string} departmentId
 * @param {Array<number>} userIds
 */
export const addEmployeesToDepartment = async (departmentId, userIds) => {
  try {
    const url = `${BASE_URL}${ENDPOINTS.ADD_EMPLOYEES_TO_DEPARTMENT.replace(':department_id', departmentId)}`;
    const token = localStorage.getItem('token');
    const response = await axios.post(url, { user_ids: userIds }, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.error || error.message || 'Failed to add employees';
    throw new Error(message);
  }
};

/**
 * Get employees for a department
 * @param {string} departmentId
 */
export const getDepartmentEmployees = async (departmentId) => {
  try {
    const url = `${BASE_URL}${ENDPOINTS.GET_DEPARTMENT_EMPLOYEES.replace(':department_id', departmentId)}`;
    const token = localStorage.getItem('token');
    const response = await axios.get(url, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    return response.data ?? [];
  } catch (error) {
    if (error.response?.status === 404) return [];
    const message = error.response?.data?.error || error.message || 'Failed to fetch employees';
    throw new Error(message);
  }
};

/**
 * Remove employees from a department
 * @param {string} departmentId
 * @param {Array<number|string>} userIds
 */
export const removeEmployeesFromDepartment = async (departmentId, userIds) => {
  try {
    const url = `${BASE_URL}${ENDPOINTS.REMOVE_DEPARTMENT_EMPLOYEES.replace(':department_id', departmentId)}`;
    const token = localStorage.getItem('token');
    const response = await axios.delete(url, {
      data: { user_ids: userIds },
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.error || error.message || 'Failed to remove employees';
    throw new Error(message);
  }
};